import React, { useState, useEffect } from 'react';
import { apiClient } from '../../utils/api';
import { Download } from 'lucide-react';
import { format } from 'date-fns';

interface Service {
  service_name: string;
  display_name: string;
  description: string;
  service_type: string;
  status: 'running' | 'stopped' | 'error';
  is_enabled: boolean;
  last_heartbeat: string | null;
  last_run: string | null;
  next_run: string | null;
  error_message: string | null;
  error_count: number;
  process_id: string | null;
}

interface ServiceLog {
  id: number;
  service_name: string;
  log_level: string;
  message: string;
  timestamp: string;
}

const ServiceManagement: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [logs, setLogs] = useState<ServiceLog[]>([]);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchServices();
    const interval = autoRefresh ? setInterval(fetchServices, 5000) : null;
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  useEffect(() => {
    if (selectedService) {
      fetchLogs(selectedService);
    }
  }, [selectedService]);

  const fetchServices = async () => {
    try {
      const data = await apiClient.get('/api/services/status');
      // The apiClient already returns response.data, so we use data directly
      if (Array.isArray(data)) {
        setServices(data);
      } else {
        console.error('Services response is not an array:', data);
        setServices([]);
      }
    } catch (err) {
      console.error('Failed to fetch services:', err);
      setServices([]);
    }
  };

  const fetchLogs = async (serviceName: string) => {
    try {
      const data = await apiClient.get(`/api/services/${serviceName}/logs`);
      setLogs(data || []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setLogs([]);
    }
  };

  const handleServiceAction = async (serviceName: string, action: 'start' | 'stop' | 'restart') => {
    setLoading(true);
    setError(null);
    try {
      await apiClient.post(`/api/services/${serviceName}/${action}`, {});
      await fetchServices();
      if (selectedService === serviceName) {
        await fetchLogs(serviceName);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || `Failed to ${action} service`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'text-green-600';
      case 'stopped': return 'text-gray-500';
      case 'error': return 'text-red-600';
      case 'scheduled': return 'text-blue-600';
      case 'disabled': return 'text-gray-400';
      default: return 'text-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running': return '●';
      case 'stopped': return '○';
      case 'error': return '⚠';
      case 'scheduled': return '◷';
      case 'disabled': return '⊘';
      default: return '?';
    }
  };

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    return date.toLocaleString();
  };

  const getLogLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      case 'info': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  const exportLogs = () => {
    if (!selectedService || logs.length === 0) return;
    
    const csvContent = [
      ['Timestamp', 'Level', 'Message'].join(','),
      ...logs.map(log => [
        new Date(log.timestamp).toISOString(),
        log.log_level,
        `"${log.message.replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedService}-logs-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="service-management">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Background Services</h2>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="mr-2"
          />
          Auto-refresh (5s)
        </label>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="flex gap-6" style={{ height: '600px' }}>
        {/* Services List - Scrollable */}
        <div className="bg-white rounded-lg shadow p-6 flex-1 flex flex-col">
          <h3 className="text-lg font-semibold mb-4">Services</h3>
          <div className="space-y-4 overflow-y-auto flex-1" style={{ maxHeight: '500px' }}>
            {services.length === 0 ? (
              <p className="text-gray-500">No services available</p>
            ) : (
              services.map((service) => (
              <div
                key={service.service_name}
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  selectedService === service.service_name ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => {
                  setSelectedService(service.service_name);
                }}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="flex items-center">
                      <span className={`text-2xl mr-2 ${getStatusColor(service.status)}`}>
                        {getStatusIcon(service.status)}
                      </span>
                      <h4 className="font-semibold">{service.display_name}</h4>
                    </div>
                    {service.description && (
                      <p className="text-sm text-gray-500 mt-1">{service.description}</p>
                    )}
                    <p className="text-sm text-gray-600 mt-1">
                      Status: <span className={getStatusColor(service.status)}>{service.status}</span>
                    </p>
                  </div>
                  {service.error_count > 0 && (
                    <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                      {service.error_count} errors
                    </span>
                  )}
                </div>

                <div className="text-xs text-gray-500 space-y-1">
                  {service.status === 'scheduled' ? (
                    // k3s CronJob service
                    <>
                      <p className="text-blue-600 font-medium">🔄 k3s CronJob (Managed by Flux)</p>
                      {service.last_run && (
                        <p>Last run: {formatTimestamp(service.last_run)}</p>
                      )}
                    </>
                  ) : (
                    // Regular background service
                    <>
                      <p>Last heartbeat: {formatTimestamp(service.last_heartbeat)}</p>
                      {service.last_run && (
                        <p>Last run: {formatTimestamp(service.last_run)}</p>
                      )}
                      {service.process_id && <p>PID: {service.process_id}</p>}
                    </>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex gap-2 mt-3">
                  {service.status === 'scheduled' ? (
                    // CronJob controls
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleServiceAction(service.service_name, 'trigger');
                        }}
                        disabled={loading}
                        className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Trigger Now
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleServiceAction(service.service_name, 'pause');
                        }}
                        disabled={loading}
                        className="px-3 py-1 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Pause
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleServiceAction(service.service_name, 'resume');
                        }}
                        disabled={loading}
                        className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Resume
                      </button>
                    </>
                  ) : (
                    // Regular service controls
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleServiceAction(service.service_name, 'start');
                        }}
                        disabled={loading || service.status === 'running'}
                        className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Start
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleServiceAction(service.service_name, 'stop');
                        }}
                        disabled={loading || service.status === 'stopped'}
                        className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Stop
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleServiceAction(service.service_name, 'restart');
                        }}
                        disabled={loading}
                        className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Restart
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
            )}
          </div>
        </div>

        {/* Service Logs - Fixed Position */}
        <div className="bg-white rounded-lg shadow p-6 flex-1 flex flex-col" style={{ position: 'sticky', top: '0' }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              {selectedService ? `${selectedService} Logs` : 'Select a Service'}
            </h3>
            {selectedService && logs.length > 0 && (
              <button
                onClick={() => exportLogs()}
                className="flex items-center px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                <Download className="w-4 h-4 mr-1" />
                Export
              </button>
            )}
          </div>
          {selectedService ? (
            <div className="space-y-2 overflow-y-auto flex-1" style={{ maxHeight: '480px' }}>
              {logs.length === 0 ? (
                <p className="text-gray-500">No logs available</p>
              ) : (
                logs.map((log) => (
                  <div key={log.id} className="border-b border-gray-100 pb-2">
                    <div className="flex justify-between items-start">
                      <span className={`text-xs font-semibold ${getLogLevelColor(log.log_level)}`}>
                        [{log.log_level}]
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-sm mt-1">{log.message}</p>
                  </div>
                ))
              )}
            </div>
          ) : (
            <p className="text-gray-500">Click on a service to view its logs</p>
          )}
        </div>
      </div>

      {/* Service Configuration */}
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <h3 className="text-lg font-semibold mb-4">Service Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium mb-2">Notification Scheduler</h4>
            <p className="text-sm text-gray-600">
              Sends overdue notifications and processes notification queue
            </p>
            <div className="mt-2 space-y-1 text-sm">
              <p>• Checks for overdue chores every 5 minutes</p>
              <p>• Processes notification queue every minute</p>
              <p>• Overdue time: 6:00 PM (configurable per chore)</p>
            </div>
          </div>
          <div>
            <h4 className="font-medium mb-2">Email Service</h4>
            <p className="text-sm text-gray-600">
              Handles email delivery for notifications
            </p>
            <div className="mt-2 space-y-1 text-sm">
              <p>• Retry failed emails up to 3 times</p>
              <p>• Exponential backoff for retries</p>
              <p>• Priority-based queue processing</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceManagement;